/**
 * CopyButton Component
 *
 * A button that copies text to clipboard and shows visual feedback.
 * Manages its own copied state and properly cleans up timers on unmount.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Copy, Check } from 'lucide-react';

interface CopyButtonProps {
  /** The text to copy to clipboard */
  text: string;
  /** Optional custom className */
  className?: string;
  /** Duration to show the checkmark (in ms) */
  feedbackDuration?: number;
}

export const CopyButton: React.FC<CopyButtonProps> = ({
  text,
  className = 'text-gray-700 hover:text-gray-900 flex-shrink-0',
  feedbackDuration = 2000,
}) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Cleanup timer on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);

      // Clear any existing timer before setting a new one
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        setCopied(false);
      }, feedbackDuration);
    } catch {
      // Clipboard access denied or not available — silently fail
      // This can happen if page is not focused or clipboard API is restricted
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={className}
      title="Click to copy"
    >
      {copied ? (
        <Check className="w-3 h-3 text-green-600" />
      ) : (
        <Copy className="w-3 h-3" />
      )}
    </button>
  );
};
